import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as metrocorpMiddleware from "middleware/metrocorp";
import * as config from "util/config";
import * as i18n from "util/i18n";
import { downloadPdf } from "util/download";
import { actions as notificationActions } from "reducers/notification";
import { NOT_AUTHORIZED, VALIDATION_ERROR } from "util/responses";
import {
    extractHoldingsPayload,
    getLabels,
    maskMovement,
    periodRange,
    SCOPES,
    toFilterId,
} from "./metrocorpOperationsV3Page.helpers";

const DATE_RANGE_MESSAGE_KEY = "metrocorp.operations.movements.validation.dateRange";
const DATE_INVALID_MESSAGE_KEY = "metrocorp.operations.movements.validation.dateInvalid";

function emptyMovements(previous) {
    return {
        filters: previous?.filters || {},
        groups: [],
    };
}

function verifyCodeFromAccount(account, accountOptions) {
    if (account?.verifyCode) {
        return account.verifyCode;
    }
    const accountId = toFilterId(account);
    if (!accountId) {
        return undefined;
    }
    const match = (accountOptions || []).find((option) => String(option.id) === String(accountId));
    return match?.verifyCode;
}

function movementsRequestParams({ account, species, period, customDates, accountOptions }) {
    const accountId = toFilterId(account);
    const verifyCode = verifyCodeFromAccount(account, accountOptions);
    const speciesId = toFilterId(species);
    return {
        ...(accountId ? { account: accountId } : {}),
        ...(accountId && verifyCode ? { verifyCode } : {}),
        species: speciesId || "all",
        period: period || "today",
        ...periodRange(period || "today", customDates),
    };
}

const BOLETO_UNAVAILABLE = "El boleto no está disponible";

function findMovementById(movements, movementId) {
    return (movements || []).reduce((found, movement) => {
        if (found) {
            return found;
        }
        if (String(movement.id) === String(movementId)) {
            return movement;
        }
        return findMovementById(movement.movementsGrouped, movementId);
    }, undefined);
}

function findMovement(groups, movementId) {
    return (groups || []).reduce((found, group) => found || findMovementById(group.movements, movementId), undefined);
}

function isPdfBase64(value) {
    if (typeof value !== "string") {
        return false;
    }
    const normalized = value.replace(/\s/g, "");
    if (!normalized || normalized.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(normalized)) {
        return false;
    }
    try {
        return window.atob(normalized).startsWith("%PDF");
    } catch (error) {
        return false;
    }
}

function boletoPayload(response) {
    return response?.data?.data ?? response?.data ?? {};
}

/** True when the API rejected params with a validation warning (not a fatal backend error). */
function isValidationWarning(response) {
    return response?.type === "W" && response?.data?.code === VALIDATION_ERROR;
}

/**
 * COR020W keeps the generic returnCode text in `message`; field errors live in `data`.
 * `processErrorMessages` translates keys, but `#{maxDays}` is filled on the client.
 */
function resolveMovementsValidationMessage(response) {
    const fieldErrors = response?.data?.data || {};
    const fieldError = fieldErrors.NO_FIELD || fieldErrors.dateFrom || fieldErrors.dateTo;
    const maxDays = config.getInteger("metrocorp.movements.maxRangeDays", 30);
    const params = { maxDays, 0: maxDays };
    const defaultRangeMessage = `El rango de movimientos debe ser de hasta ${maxDays} días y no incluir fechas futuras`;

    if (!fieldError) {
        return response?.data?.message || i18n.get("global.unexpectedError");
    }
    const fieldErrorText = String(fieldError);
    if (fieldErrorText.includes(DATE_RANGE_MESSAGE_KEY) || fieldErrorText.includes("#{maxDays}")) {
        return i18n.get(DATE_RANGE_MESSAGE_KEY, defaultRangeMessage, params);
    }
    if (fieldErrorText.includes(DATE_INVALID_MESSAGE_KEY)) {
        return i18n.get(DATE_INVALID_MESSAGE_KEY, "Fecha de movimientos inválida");
    }
    return i18n.replaceParams(fieldErrorText, params);
}

function useMetrocorpMovements({
    dispatch,
    section,
    holdings,
    holdingsSection,
    shouldHideAmounts,
    metrocorpV3Enabled,
    metrocorpMovementsEnabled,
    handleHoldingsError,
}) {
    const [movements, setMovements] = useState(null);
    const [movementsLoading, setMovementsLoading] = useState(false);
    const [movementsFiltering, setMovementsFiltering] = useState(false);
    const [movementsFetched, setMovementsFetched] = useState(false);
    const [movementsPeriod, setMovementsPeriod] = useState("today");
    const [movementsCustomDates, setMovementsCustomDates] = useState(undefined);
    const [movementsFiltersActive, setMovementsFiltersActive] = useState(false);
    const [movementsResetVersion, setMovementsResetVersion] = useState(0);
    const [movementsError, setMovementsError] = useState(false);
    const [downloadingMovementId, setDownloadingMovementId] = useState(null);
    const lastRequestIdRef = useRef(0);
    const downloadingRef = useRef(false);
    const selectedAccount = holdings?.holdingsFilters?.selectedAccount;
    const selectedAccountId = toFilterId(selectedAccount);
    const selectedVerifyCode = selectedAccount?.verifyCode;

    /**
     * Fetches movements from the API, ignoring stale responses via requestId.
     */
    const requestMovements = useCallback(
        (params, { asFilter = false } = {}) => {
            const requestId = lastRequestIdRef.current + 1;
            lastRequestIdRef.current = requestId;
            setMovementsLoading(true);
            setMovementsFiltering(asFilter);
            setMovementsError(false);
            return metrocorpMiddleware
                .listMovements(params)
                .then((response) => {
                    if (requestId !== lastRequestIdRef.current) {
                        return;
                    }
                    if (isValidationWarning(response)) {
                        setMovementsFetched(true);
                        setMovementsError(true);
                        setMovements((current) => emptyMovements(current));
                        dispatch(
                            notificationActions.showNotification(
                                resolveMovementsValidationMessage(response),
                                "error",
                                SCOPES,
                            ),
                        );
                        return;
                    }
                    if (handleHoldingsError(response)) {
                        setMovementsFetched(true);
                        setMovementsError(true);
                        setMovements((current) => current || emptyMovements(current));
                        return;
                    }
                    setMovements(extractHoldingsPayload(response));
                    setMovementsFetched(true);
                })
                .catch((error) => {
                    if (requestId !== lastRequestIdRef.current) {
                        return;
                    }
                    setMovementsFetched(true);
                    setMovementsError(true);
                    setMovements((current) => current || emptyMovements(current));
                    const message =
                        error?.data?.code === NOT_AUTHORIZED
                            ? i18n.get("returnCode.COR005E")
                            : i18n.get("global.unexpectedError");
                    dispatch(notificationActions.showNotification(message, "error", SCOPES));
                })
                .finally(() => {
                    if (requestId !== lastRequestIdRef.current) {
                        return;
                    }
                    setMovementsLoading(false);
                    setMovementsFiltering(false);
                });
        },
        [dispatch, handleHoldingsError],
    );

    const notifyBoletoUnavailable = useCallback(() => {
        dispatch(
            notificationActions.showNotification(
                i18n.get("metrocorp.movements.boleto.unavailable", BOLETO_UNAVAILABLE),
                "error",
                SCOPES,
            ),
        );
    }, [dispatch]);

    const onDownloadMovement = useCallback(
        (movementId) => {
            if (downloadingRef.current) {
                return undefined;
            }
            const movement = findMovement(movements?.groups, movementId);
            const formulario = movement?.formulario;
            const numero = movement?.numero;
            const idBoleto = movement?.idBoleto;
            const date = movement?.date;
            const account = selectedAccountId;
            const verifyCode = selectedVerifyCode;
            if (
                !formulario ||
                numero == null ||
                numero === "" ||
                idBoleto == null ||
                idBoleto === "" ||
                !account ||
                !verifyCode ||
                !date ||
                date === "-"
            ) {
                notifyBoletoUnavailable();
                return undefined;
            }
            downloadingRef.current = true;
            setDownloadingMovementId(movementId);
            return metrocorpMiddleware
                .downloadBoleto({ formulario, numero, idBoleto, account, verifyCode, date })
                .then((response) => {
                    const payload = boletoPayload(response);
                    if (response?.type === "W" || !isPdfBase64(payload.content)) {
                        notifyBoletoUnavailable();
                        return;
                    }
                    downloadPdf(payload.fileName || `Boleto-${numero}.pdf`, payload.content);
                    dispatch(
                        notificationActions.showNotification(i18n.get("global.successDownload"), "success", SCOPES),
                    );
                })
                .catch(() => {
                    notifyBoletoUnavailable();
                })
                .finally(() => {
                    downloadingRef.current = false;
                    setDownloadingMovementId(null);
                });
        },
        [dispatch, movements, notifyBoletoUnavailable, selectedAccountId, selectedVerifyCode],
    );

    /**
     * Clears cached movements when the selected holdings account changes.
     */
    useEffect(() => {
        setMovementsFetched(false);
        setMovements(null);
    }, [selectedAccountId]);

    /**
     * Loads initial movements when entering the section (once per account).
     */
    useEffect(() => {
        if (!metrocorpV3Enabled || !metrocorpMovementsEnabled || section !== "movements" || movementsFetched) {
            return undefined;
        }
        requestMovements(
            movementsRequestParams({
                account: selectedAccountId ? { value: selectedAccountId, verifyCode: selectedVerifyCode } : null,
                species: "all",
                period: "today",
            }),
        );
        return undefined;
    }, [
        metrocorpMovementsEnabled,
        metrocorpV3Enabled,
        movementsFetched,
        requestMovements,
        section,
        selectedAccountId,
        selectedVerifyCode,
    ]);

    const onFilterMovements = useCallback(
        (values) => {
            setMovementsFiltersActive(true);
            const period = values?.period || "today";
            setMovementsPeriod(period);
            setMovementsCustomDates(period === "custom" ? values?.customDates : undefined);
            requestMovements(
                movementsRequestParams({
                    account: values?.account,
                    species: values?.species,
                    period,
                    customDates: values?.customDates,
                    accountOptions: movements?.filters?.accountOptions,
                    movementsDateRange: values?.movementsDateRange,
                }),
                { asFilter: true },
            );
        },
        [movements?.filters?.accountOptions, requestMovements],
    );

    const onClearMovementsFilters = useCallback(() => {
        setMovementsFiltersActive(false);
        setMovementsPeriod("today");
        setMovementsCustomDates(undefined);
        setMovementsResetVersion((version) => version + 1);
        requestMovements(
            {
                species: "all",
                period: "today",
                movementsDateRange: null,
                ...periodRange("today"),
            },
            { asFilter: true },
        );
    }, [requestMovements]);

    const movementsSection = useMemo(() => {
        if (!metrocorpMovementsEnabled || !holdingsSection || !movements) {
            return undefined;
        }
        const filters = movements.filters || {};
        const allSpeciesLabel = getLabels("holdings.filter.species.all");
        const maxDaysOfRange = config.getInteger("metrocorp.movements.maxRangeDays", 30);
        return {
            ...holdingsSection,
            filters: {
                accountOptions: filters.accountOptions || [],
                speciesOptions: (filters.speciesOptions || []).map((option) => {
                    if (option.id === "all") {
                        return { ...option, label: allSpeciesLabel };
                    }
                    return option;
                }),
                initialValues: {
                    account: filters.selectedAccount || null,
                    species: filters.selectedSpecies || { value: "all", label: allSpeciesLabel },
                    period: movementsPeriod,
                    customDates: movementsCustomDates,
                    movementsDateRange: filters.movementsDateRange || null,
                },
                initialPeriod: "today",
                onSubmit: onFilterMovements,
                filtersActive: movementsFiltersActive,
                onClearFilters: onClearMovementsFilters,
                resetVersion: movementsResetVersion,
                loading: movementsFiltering,
                maxDaysOfRange,
            },
            groups: (movements.groups || []).map((group) => ({
                ...group,
                label: getLabels(`movements.group.${group.id}`) || group.label,
                movements: (group.movements || []).map((movement) => maskMovement(movement, shouldHideAmounts)),
            })),
            onDownloadMovement,
            downloadingMovementId,
        };
    }, [
        holdingsSection,
        downloadingMovementId,
        metrocorpMovementsEnabled,
        movements,
        movementsFiltering,
        movementsFiltersActive,
        movementsCustomDates,
        movementsPeriod,
        movementsResetVersion,
        onClearMovementsFilters,
        onDownloadMovement,
        onFilterMovements,
        shouldHideAmounts,
    ]);

    return {
        movementsSection,
        movementsLoading,
        movementsError,
    };
}

export default useMetrocorpMovements;
